
import React from "react";
import moment from "moment";
import withCandidate from "./withCandidate";

const TemplateBody = ({ candidate }) => {
  const languages = candidate?.languages || [];
  const skills = candidate?.skills || [];
  const work = candidate?.workExperiences || [];
  const educations = candidate?.educations || [];
  const references = candidate?.references || [];
  const interests = candidate?.interests || [];

  // Helpers (purs, sans effets)
  const formatRange = (start, end, fmt = "MMM YYYY") => {
    const s = start ? moment(start).format(fmt) : "";
    const e = end ? moment(end).format(fmt) : "Aujourd’hui";
    return s && e ? `${s} — ${e}` : s || e || "";
  };

  const guessPctFromLevel = (lvl) => {
    const L = String(lvl || "").toUpperCase().trim();
    const map = { C2: 96, C1: 86, B2: 76, B1: 62, A2: 46, A1: 32 };
    return map[L] ?? 56;
  };

  return (
    <>
      <style>{`
        /* =====================================================================================
           VARIABLES DE THÈME — appliquées via .template-page[data-theme="..."]
        ===================================================================================== */
        .template-page {
          --accent: #2563eb;    /* bleu titre */
          --ink: #111827;       /* texte principal */
          --muted: #6b7280;     /* texte secondaire */
          --paper: #ffffff;     /* fond feuille */
          --side: #f5f7fb;      /* fond bande latérale claire */
          --chip: #e5e7eb;
          --dash: #e5e7eb;      /* séparateurs doux */
          --border: #d1d5db;    /* cadre feuille */
        }

        .template-page[data-theme="blue-pro"] {
          --accent:#2379B8; --ink:#0f172a; --muted:#6b7280; --paper:#ffffff;
          --side:#f1f8ff; --chip:#e8eef6; --dash:#dbeafe; --border:#cbd5e1;
        }
        .template-page[data-theme="slate"] {
          --accent:#64748b; --ink:#1e293b; --muted:#475569; --paper:#ffffff;
          --side:#f3f4f6; --chip:#e5e7eb; --dash:#e2e8f0; --border:#cbd5e1;
        }
        .template-page[data-theme="emerald"] {
          --accent:#10b981; --ink:#064e3b; --muted:#5f6b67; --paper:#ffffff;
          --side:#ecfdf5; --chip:#d1fae5; --dash:#c7eedf; --border:#bfe3d6;
        }
        .template-page[data-theme="royal"] {
          --accent:#7c3aed; --ink:#1e1b4b; --muted:#6b6b9b; --paper:#ffffff;
          --side:#f5f1ff; --chip:#ede9fe; --dash:#e5ddff; --border:#ddd6fe;
        }
        .template-page[data-theme="bordeaux"] {
          --accent:#b91c1c; --ink:#1f1f1f; --muted:#7a6565; --paper:#ffffff;
          --side:#fef2f2; --chip:#fee2e2; --dash:#fbd5d5; --border:#fecaca;
        }
        .template-page[data-theme="noir-or"] {
          --accent:#d4a017; --ink:#e5e5e5; --muted:#bfbfbf; --paper:#1b1b1b;
          --side:#141414; --chip:#2a2a2a; --dash:#2c2c2c; --border:#333333;
        }
        .template-page[data-theme="bleu-clair"] {
          --accent:#3b82f6; --ink:#1e293b; --muted:#64748b; --paper:#ffffff;
          --side:#eff6ff; --chip:#dbeafe; --dash:#dbeafe; --border:#bfdbfe;
        }
        .template-page[data-theme="orange"] {
          --accent:#f97316; --ink:#1a1a1a; --muted:#6b7280; --paper:#ffffff;
          --side:#fff7ed; --chip:#fed7aa; --dash:#ffe1c3; --border:#fdba74;
        }
        .template-page[data-theme="teal"] {
          --accent:#0ea5a4; --ink:#0f172a; --muted:#5b7a85; --paper:#ffffff;
          --side:#f0fdfa; --chip:#ccfbf1; --dash:#c6f7f1; --border:#99f6e4;
        }
        .template-page[data-theme="forest"] {
          --accent:#22c55e; --ink:#10231b; --muted:#5f776e; --paper:#ffffff;
          --side:#ecfdf5; --chip:#d1fae5; --dash:#c7f0de; --border:#a7f3d0;
        }
        .template-page[data-theme="rose"] {
          --accent:#e11d48; --ink:#2b0a13; --muted:#a36a7a; --paper:#ffffff;
          --side:#fff1f2; --chip:#ffe4e6; --dash:#ffd7df; --border:#fecdd3;
        }
        .template-page[data-theme="indigo"] {
          --accent:#4f46e5; --ink:#1e1b4b; --muted:#717ab9; --paper:#ffffff;
          --side:#eef2ff; --chip:#e0e7ff; --dash:#dbe1ff; --border:#c7d2fe;
        }
        .template-page[data-theme="malibaara"] {
          --accent:#2379B8; --ink:#0f172a; --muted:#6b7280; --paper:#ffffff;
          --side:#f1f8ff; --chip:#e8eef6; --dash:#dbeafe; --border:#cbd5e1;
        }

        /* =====================================================================================
           LAYOUT — scoping strict (aucun style global)
        ===================================================================================== */
        .tpl-classic {
          width: 210mm;
          min-height: 297mm;
          margin: 10px auto;
          background: var(--paper);
          color: var(--ink);
          border: 1px solid var(--border);
          box-shadow: 0 10px 30px rgba(0,0,0,.12);
          font-family: Arial, Helvetica, sans-serif;
          padding: 24px 24px 28px;
          position: relative;
        }

        body { background:#222; }

        /* Bande supérieure : nom + avatar */
        .top {
          display: grid;
          grid-template-columns: 1fr 140px;
          gap: 16px;
          align-items: center;
          margin-bottom: 16px;
        }
        .big-name {
          font-size: 30px;
          font-weight: 900;
          letter-spacing: .4px;
          color: var(--ink);
          text-transform: uppercase;
        }
        .avatar-box {
          justify-self: end;
          width: 120px; height: 120px; border-radius: 50%;
          overflow: hidden; background: #ddd;
          box-shadow: 0 6px 18px rgba(0,0,0,.18);
        }
        .avatar-box img { width: 100%; height: 100%; object-fit: cover; display:block; }

        /* Grille principale : colonne gauche étroite + colonne droite large */
        .grid {
          display: grid;
          grid-template-columns: 250px 1fr;
          gap: 24px;
          align-items: start;
        }

        /* Colonne gauche */
        .left {
          background: var(--side);
          border: 1px solid var(--dash);
          border-radius: 8px;
          padding: 16px 18px;
        }
        .left .block + .block { margin-top: 18px; }
        .block-title {
          font-weight: 800; color: var(--ink); margin-bottom: 8px;
        }
        .kv { font-size: 13px; color: var(--ink); line-height: 1.55; }
        .kv .row { display: grid; grid-template-columns: 18px 1fr; gap: 8px; align-items: center; }
        .kv .row + .row { margin-top: 6px; }
        .kv a { color: inherit; text-decoration: none; }

        .skill-list { margin: 0; padding-left: 16px; }
        .skill-list li { font-size: 13px; margin: 4px 0; }

        /* Langues */
        .lang-item { margin-bottom: 10px; }
        .lang-head { display:flex; justify-content:space-between; font-size:12px; color:var(--ink); }
        .lang-note { color: var(--muted); }
        .bar { height: 8px; background:#e5e7eb; border-radius:999px; overflow:hidden; margin-top:6px; }
        .bar > span { display:block; height:100%; background: var(--ink); }

        /* Colonne droite */
        .right { padding-top: 2px; }
        .section + .section { margin-top: 16px; }
        .section-title {
          color: var(--ink);
          font-size: 14px;
          font-weight: 900;
          margin-bottom: 8px;
          display:flex; align-items:center; gap:10px;
        }
        .section-title .rule {
          flex: 1; height: 1px; background: var(--dash);
        }
        .para { font-size: 13px; color: var(--ink); line-height: 1.6; }

        /* Expériences (avec puces) */
        .job { margin: 12px 0; }
        .job .head { font-weight: 800; color: var(--ink); }
        .job .sub { font-size: 12px; color: var(--muted); margin-top: 2px; }
        .job ul { margin: 6px 0 0 18px; }
        .job li { font-size: 13px; margin: 3px 0; color: var(--ink); list-style: disc; }

        .edu-item { margin: 10px 0; }
        .interest-list { margin: 0; padding-left: 16px; }
        .interest-list li { font-size: 13px; margin: 4px 0; }
      `}</style>

      <div className="tpl-classic">
        {/* Bande supérieure : Nom & avatar */}
        <div className="top">
          <div className="big-name">
            {candidate?.firstName} {candidate?.lastName}
          </div>
          <div className="avatar-box">
            <img
              crossOrigin="anonymous"
              src={candidate?.profilePictureUrl || "/assets/images/avatar/avatar2.png"}
              alt=""
            />
          </div>
        </div>

        {/* Grille principale */}
        <div className="grid">
          {/* Colonne gauche */}
          <aside className="left">
            {/* Coordonnées */}
            <div className="block">
              <div className="block-title">Coordonnées</div>
              <div className="kv">
                {candidate?.address && (
                  <div className="row"><span>📍</span><span>{candidate.address}</span></div>
                )}
                {candidate?.phoneNumber1 && (
                  <div className="row"><span>📞</span><span>{candidate.phoneNumber1}</span></div>
                )}
                {candidate?.email && (
                  <div className="row">
                    <span>✉️</span>
                    <a href={`mailto:${candidate.email}`}>{candidate.email}</a>
                  </div>
                )}
              </div>
            </div>

            {/* Langues */}
            {!!languages.length && (
              <div className="block">
                <div className="block-title">Langue</div>
                {languages.map((l, i) => {
                  const pct = l?.percentage
                    ? Math.max(0, Math.min(100, Number(l.percentage)))
                    : guessPctFromLevel(l?.level || l?.speakingLevel || l?.writingLevel);
                  const lvl = l?.level || l?.speakingLevel || l?.writingLevel || "";
                  return (
                    <div className="lang-item" key={i}>
                      <div className="lang-head">
                        <span>{l?.name} - Écrit</span>
                        <span className="lang-note">{l?.writingLevel || ""}</span>
                      </div>
                      <div className="bar"><span style={{ width: `${l?.writingLevel * 10}%` }} /></div>
                      <div className="lang-head" style={{marginTop: "10px"}}>
                        <span>{l?.name} - Parlé</span>
                        <span className="lang-note">{l?.speakingLevel || ""}</span>
                      </div>
                      <div className="bar"><span style={{ width: `${l?.speakingLevel * 10}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Compétences */}
            {!!skills.length && (
              <div className="block">
                <div className="block-title">Compétences</div>
                <ul className="skill-list">
                  {skills.map((s, i) => <li key={i}>{s?.title}</li>)}
                </ul>
              </div>
            )}
          </aside>

          {/* Colonne droite */}
          <main className="right">
            {/* Profil professionnel */}
            <section className="section">
              <div className="section-title">
                <span>Profil Professionnel</span><span className="rule" />
              </div>
              <div
                className="para"
                dangerouslySetInnerHTML={{ __html: candidate?.summaryDescription || "" }}
              />
            </section>

            {/* Parcours professionnel */}
            {!!work.length && (
              <section className="section">
                <div className="section-title">
                  <span>Parcours Professionnel</span><span className="rule" />
                </div>

                {work.map((job, i) => (
                  <div className="job" key={i}>
                    <div className="head">{job?.title}</div>
                    <div className="sub">
                      {job?.company ? `${job.company} | ` : ""}
                      {formatRange(job?.startDate, job?.endDate)}
                    </div>

                    {job?.description && <div className="para">{job.description}</div>}

                    {Array.isArray(job?.achievements) && job.achievements.length > 0 && (
                      <ul>
                        {job.achievements.map((a, k) => <li key={k}>{a}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </section>
            )}

            {/* Formation */}
            {!!educations.length && (
              <section className="section">
                <div className="section-title">
                  <span>Formation</span><span className="rule" />
                </div>
                {educations.map((edu, i) => (
                  <div className="edu-item" key={i}>
                    <div className="head" style={{ fontWeight: 800 }}>{edu?.degree}</div>
                    <div className="sub" style={{ color: "var(--muted)", fontSize: 12 }}>
                      {edu?.institution}
                      {edu?.description} {(edu?.startYear || edu?.endYear) &&
                        ` — ${edu?.startYear || ""}${edu?.endYear ? ` – ${edu.endYear}` : ""}`}
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* References */}
            {!!references.length && (
              <section className="section">
                <div className="section-title">
                  <span>Références</span><span className="rule" />
                </div>

                {references.map((ref, i) => (
                  <div className="job" key={i}>
                    <div className="head">{ref?.fullName}</div>
                    <div className="sub">
                      {ref?.title}
                    </div>

                    <div className="para">
                      Tel: {ref.phoneNumber1} / {ref.phoneNumber2} <br />
                      Email: {ref.email}
                    </div>
                  </div>
                ))}
              </section>
            )}

          </main>
        </div>
      </div>
    </>
  );
};

export default withCandidate(TemplateBody, "Simple Deux Colonnes 2");
